import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { useUserProfile } from '../hooks/useUserProfile';
import { db } from '../firebase';
import { COUNTRIES, citiesForCountry, PHONE_DIAL_CODES } from '../data/countriesCities';
import { getAppNavItems } from '../utils/appNavItems';
import { SBA } from '../theme';

const EDUCATION_LEVELS = [
  'Middle school',
  'Matric / O-Levels (incomplete)',
  'Matric / O-Levels (complete)',
  'Intermediate / A-Levels (incomplete)',
  'Left school before matric',
  'Other',
];

export default function EditProfilePage() {
  const { currentUser, isAdmin, hasSubscription } = useAuth();
  const location = useLocation();
  const { profile, loading } = useUserProfile(currentUser?.uid);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('success');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const navItems = getAppNavItems(isAdmin, location.pathname, { hasSubscription });

  useEffect(() => {
    if (profile && !form) {
      setForm({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phoneCountryCode: profile.phoneCountryCode || '+92',
        phoneLocal: (profile.phone || '').replace(profile.phoneCountryCode || '', ''),
        dob: profile.dob || '',
        country: profile.country || '',
        city: profile.city || '',
        govtIdType: profile.govtIdType || 'CNIC',
        govtIdNumber: profile.govtIdNumber || '',
        educationLevel: profile.educationLevel || '',
        educationDetails: profile.educationDetails || '',
        fatherName: profile.fatherName || '',
        fatherPhone: profile.fatherPhone || '',
        address: profile.address || '',
      });
    }
  }, [profile, form]);

  function update(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'country') next.city = '';
      return next;
    });
    setMessage('');
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!currentUser?.uid || !form) return;

    const required = [
      form.firstName,
      form.lastName,
      form.phoneLocal,
      form.dob,
      form.country,
      form.city,
      form.govtIdNumber,
      form.educationLevel,
      form.fatherName,
    ];
    if (required.some((v) => !String(v).trim())) {
      setMessageTone('error');
      setMessage('Please fill all required fields (marked *) to complete your profile.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        phoneCountryCode: form.phoneCountryCode,
        phone: `${form.phoneCountryCode}${form.phoneLocal.replace(/[^\d]/g, '')}`,
        dob: form.dob,
        country: form.country,
        city: form.city,
        govtIdType: form.govtIdType,
        govtIdNumber: form.govtIdNumber.trim(),
        educationLevel: form.educationLevel,
        educationDetails: form.educationDetails.trim(),
        fatherName: form.fatherName.trim(),
        fatherPhone: form.fatherPhone.trim(),
        address: form.address.trim(),
        profileComplete: true,
        profileUpdatedAt: serverTimestamp(),
      });
      setMessageTone('success');
      setMessage('Profile saved. Your profile is now complete.');
    } catch (err) {
      console.error(err);
      setMessageTone('error');
      setMessage(err?.message || 'Could not save profile. Try again.');
    } finally {
      setSaving(false);
    }
  }

  const cities = form ? citiesForCountry(form.country) : [];

  return (
    <AppShell navItems={navItems}>
      <div style={s.wrap}>
        <div style={s.headRow}>
          <div>
            <p style={s.eyebrow}>My Profile</p>
            <h1 style={s.title}>Edit profile</h1>
            <p style={s.sub}>
              Keep your details exactly as on your government ID — they are used for your GED
              records.
            </p>
          </div>
          <button type="button" style={s.pwBtn} onClick={() => setShowPasswordModal(true)}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>key</span>
            Change password
          </button>
        </div>

        {profile && profile.profileComplete !== true ? (
          <div className="sba-blink" style={s.incompleteBanner}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: SBA.goldStrong }}>
              info
            </span>
            Complete all required fields below to finish your profile.
          </div>
        ) : null}

        {loading || !form ? (
          <p>Loading profile…</p>
        ) : (
          <form onSubmit={handleSave} style={s.card}>
            <h2 style={s.sectionTitle}>Personal information</h2>
            <div style={s.regBox}>
              <p style={s.regLabel}>Registration number</p>
              <p style={s.regValue}>
                {profile?.registrationNumber || 'Assigned after enrollment / payment'}
              </p>
            </div>
            <div style={s.grid}>
              <label style={s.label}>
                First name (as on govt. ID) *
                <input
                  style={s.input}
                  value={form.firstName}
                  onChange={(e) => update('firstName', e.target.value)}
                />
              </label>
              <label style={s.label}>
                Last name (as on govt. ID) *
                <input
                  style={s.input}
                  value={form.lastName}
                  onChange={(e) => update('lastName', e.target.value)}
                />
              </label>
              <label style={s.label}>
                Date of birth *
                <input
                  type="date"
                  style={s.input}
                  value={form.dob}
                  onChange={(e) => update('dob', e.target.value)}
                />
              </label>
              <label style={s.label}>
                Phone number *
                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 8 }}>
                  <select
                    style={s.input}
                    value={form.phoneCountryCode}
                    onChange={(e) => update('phoneCountryCode', e.target.value)}
                  >
                    {PHONE_DIAL_CODES.map(({ code, label }) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    style={s.input}
                    value={form.phoneLocal}
                    onChange={(e) => update('phoneLocal', e.target.value)}
                    placeholder="3XX XXXXXXX"
                  />
                </div>
              </label>
              <label style={s.label}>
                Country *
                <select
                  style={s.input}
                  value={form.country}
                  onChange={(e) => update('country', e.target.value)}
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label style={s.label}>
                City *
                <select
                  style={s.input}
                  value={form.city}
                  onChange={(e) => update('city', e.target.value)}
                  disabled={!form.country}
                >
                  <option value="">{form.country ? 'Select city' : 'Select country first'}</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ ...s.label, gridColumn: '1 / -1' }}>
                Home address
                <input
                  style={s.input}
                  value={form.address}
                  onChange={(e) => update('address', e.target.value)}
                  placeholder="Street, area, postal code"
                />
              </label>
            </div>

            <h2 style={s.sectionTitle}>Identity document</h2>
            <div style={s.grid}>
              <label style={s.label}>
                ID type *
                <select
                  style={s.input}
                  value={form.govtIdType}
                  onChange={(e) => update('govtIdType', e.target.value)}
                >
                  <option value="CNIC">CNIC</option>
                  <option value="Passport">Passport</option>
                  <option value="Govt ID">Other government ID</option>
                </select>
              </label>
              <label style={s.label}>
                {form.govtIdType} number *
                <input
                  style={s.input}
                  value={form.govtIdNumber}
                  onChange={(e) => update('govtIdNumber', e.target.value)}
                  placeholder={form.govtIdType === 'CNIC' ? 'XXXXX-XXXXXXX-X' : 'Document number'}
                />
              </label>
            </div>

            <h2 style={s.sectionTitle}>Education</h2>
            <div style={s.grid}>
              <label style={s.label}>
                Highest education level *
                <select
                  style={s.input}
                  value={form.educationLevel}
                  onChange={(e) => update('educationLevel', e.target.value)}
                >
                  <option value="">Select level</option>
                  {EDUCATION_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label style={s.label}>
                Education details
                <input
                  style={s.input}
                  value={form.educationDetails}
                  onChange={(e) => update('educationDetails', e.target.value)}
                  placeholder="School / board / year"
                />
              </label>
            </div>

            <h2 style={s.sectionTitle}>Guardian information</h2>
            <div style={s.grid}>
              <label style={s.label}>
                Father / guardian name *
                <input
                  style={s.input}
                  value={form.fatherName}
                  onChange={(e) => update('fatherName', e.target.value)}
                />
              </label>
              <label style={s.label}>
                Father / guardian phone
                <input
                  type="tel"
                  style={s.input}
                  value={form.fatherPhone}
                  onChange={(e) => update('fatherPhone', e.target.value)}
                  placeholder="+92 3XX XXXXXXX"
                />
              </label>
            </div>

            {message ? <div style={s.banner(messageTone)}>{message}</div> : null}

            <button type="submit" disabled={saving} style={s.saveBtn(saving)}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </form>
        )}
      </div>

      {showPasswordModal ? (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      ) : null}
    </AppShell>
  );
}

const s = {
  wrap: { maxWidth: 860 },
  headRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: SBA.goldStrong,
    marginBottom: 8,
  },
  title: {
    fontFamily: SBA.fontDisplay,
    fontSize: 26,
    fontWeight: 800,
    color: SBA.navy,
    marginBottom: 6,
  },
  sub: { fontSize: 14, color: SBA.onSurfaceVariant, maxWidth: 520, lineHeight: 1.5 },
  pwBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 16px',
    borderRadius: 12,
    border: `1.5px solid ${SBA.navy}`,
    background: '#fff',
    color: SBA.navy,
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  incompleteBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderRadius: 14,
    background: 'rgba(197,160,70,0.1)',
    border: '1.5px solid rgba(197,160,70,0.5)',
    fontSize: 13,
    fontWeight: 600,
    color: SBA.navy,
    marginBottom: 20,
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 8px 28px rgba(27,54,93,0.07)',
    padding: 24,
  },
  sectionTitle: {
    margin: '0 0 14px',
    fontSize: 15,
    fontWeight: 800,
    color: SBA.navy,
    paddingBottom: 8,
    borderBottom: '1px solid #eef2f7',
  },
  regBox: {
    marginBottom: 18,
    padding: '12px 14px',
    borderRadius: 12,
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
  },
  regLabel: {
    margin: 0,
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  regValue: {
    margin: '4px 0 0',
    fontFamily: 'ui-monospace, monospace',
    fontSize: 15,
    fontWeight: 800,
    color: SBA.navy,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 14,
    marginBottom: 24,
  },
  label: { display: 'grid', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' },
  input: {
    padding: '11px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    background: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  },
  banner: (tone) => ({
    marginBottom: 14,
    padding: '12px 14px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    ...(tone === 'error'
      ? { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }
      : { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }),
  }),
  saveBtn: (disabled) => ({
    padding: '14px 22px',
    borderRadius: 12,
    border: 'none',
    background: disabled ? '#94a3b8' : `linear-gradient(135deg, ${SBA.navy} 0%, ${SBA.navyDeep} 100%)`,
    color: '#fff',
    fontWeight: 800,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};
